import {
	HttpClient,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from "@angular/common/http";
import {
	Inject,
	Injectable,
	InjectionToken,
	Injector,
	Optional,
	Type,
} from "@angular/core";
import { Observable } from "rxjs";

import { ApiPrefixInterceptor } from "./interceptor/prefix.interceptor";

declare module "@angular/common/http/http" {
	export interface HttpClient {
		/**
		 * Do not use API prefix for this request.
		 * @return The new instance.
		 */
		disableApiPrefix(): HttpClient;
	}
}

class HttpInterceptorHandler implements HttpHandler {
	constructor(
		private next: HttpHandler,
		private interceptor: HttpInterceptor
	) {}

	handle(request: HttpRequest<any>): Observable<HttpEvent<any>> {
		return this.interceptor.intercept(request, this.next);
	}
}

export const HTTP_DYNAMIC_INTERCEPTORS = new InjectionToken<HttpInterceptor>(
	"HTTP_DYNAMIC_INTERCEPTORS"
);

/**
 * Extends HttpClient with per request configuration using dynamic interceptors.
 */
@Injectable({
	providedIn: "root",
})
export class HttpService extends HttpClient {
	constructor(
		private httpHandler: HttpHandler,
		private injector: Injector,
		@Optional()
		@Inject(HTTP_DYNAMIC_INTERCEPTORS)
		private interceptors: HttpInterceptor[] = []
	) {
		super(httpHandler);

		if (!this.interceptors) {
			this.interceptors = [this.injector.get(ApiPrefixInterceptor)];
		}
	}

	disableApiPrefix(): HttpClient {
		return this.removeInterceptor(ApiPrefixInterceptor);
	}

	// Override the original method to wire interceptors when triggering the request.
	request(method?: any, url?: any, options?: any): any {
		const handler = this.interceptors.reduceRight(
			(next, interceptor) =>
				new HttpInterceptorHandler(next, interceptor),
			this.httpHandler
		);
		return new HttpClient(handler).request(method, url, options);
	}

	private removeInterceptor(
		interceptorType: Type<HttpInterceptor>
	): HttpService {
		return new HttpService(
			this.httpHandler,
			this.injector,
			this.interceptors.filter((i) => !(i instanceof interceptorType))
		);
	}

	private addInterceptor(interceptor: HttpInterceptor): HttpService {
		return new HttpService(
			this.httpHandler,
			this.injector,
			this.interceptors.concat([interceptor])
		);
	}
}
